import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { isNilOrEmpty } from 'ramda-adjunct';
import { useHttpCalls } from '../api/useHttpCalls';
import { selectGridCode, selectIsResizing, selectQueryId } from '../store/createEagGridStore';
import { useEagGridSelector } from '../store/useEagGridSelector';
import { useEagGridStore } from '../store/useEagGridStore';
import type { ActivePreset, FeGridSettings, PresetDetail, VirtualPreset } from '../types/Api';
import type { EagColumnState } from '../types/EagColumnState';
import type { Filters } from '../types/Filters';
import { filterTechnicalArray } from '../utils/filterTechnicalArray';
import { getPresetStates } from '../utils/getPresetStates';
import { isCustomPresetDirty } from '../utils/isCustomPresetDirty';
import { isPresetColumnsSettingsInvalid } from '../utils/isPresetColumnsSettingsInvalid';
import { isPresetCustom } from '../utils/isPresetCustom';
import { isVirtualPresetEqual } from '../utils/isVirtualPresetEqual';
import { getSearchParamsByGridCode } from '../utils/url/getSearchParamsByGridCode';
type UsePresetSyncProps = {
  columnStates: EagColumnState[];
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  activePreset: ActivePreset;
  settings: FeGridSettings;
};

/**
 * Bridges preset state ↔ EagGrid column/filter/sort state.
 *
 * - When activePreset changes → apply columnsSettings + sort from dataQuery atomically
 * - When columnStates/filters/settings change → PUT virtual-preset with new derived state
 *
 * Reads gridCode from DataGridContext and dataQueryId from URL search params internally,
 * eliminating the need to drill updateVirtualPreset through props.
 */
export const usePresetSync = props => {
  const $ = _c(15);
  const http = useHttpCalls();
  const store = useEagGridStore();
  const gridCode = useEagGridSelector(selectGridCode);
  const isResizing = useEagGridSelector(selectIsResizing);
  const queryId = useEagGridSelector(selectQueryId);
  const lastVirtualPresetState = useRef(null);
  const hasPresetBeenApplied = useRef(false);
  const gridCodeRef = useRef(gridCode);
  const settingsRef = useRef(props.settings);
  const activePresetRef = useRef(props.activePreset);
  const httpRef = useRef(http);
  const storeRef = useRef(store);
  let t0;
  if ($[0] !== gridCode || $[1] !== http || $[2] !== props.activePreset || $[3] !== props.settings || $[4] !== store) {
    t0 = () => {
      gridCodeRef.current = gridCode;
      settingsRef.current = props.settings;
      activePresetRef.current = props.activePreset;
      httpRef.current = http;
      storeRef.current = store;
    };
    $[0] = gridCode;
    $[1] = http;
    $[2] = props.activePreset;
    $[3] = props.settings;
    $[4] = store;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  useEffect(t0);
  const columnsSettings = props.activePreset?.columnsSettings;
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const activePreset = activePresetRef.current;
      if (!activePreset) {
        return;
      }
      const cols = activePreset.columnsSettings;
      if (isNilOrEmpty(cols)) {
        return;
      }
      storeRef.current.applyPresetColumnsAndSort(cols);
      hasPresetBeenApplied.current = true;
      if (!isPresetCustom(activePreset)) {
        const seedQueryId = getSearchParamsByGridCode(gridCodeRef.current)?.queryId ?? "";
        lastVirtualPresetState.current = {
          columnsSettings: filterTechnicalArray(cols),
          gridSettings: settingsRef.current,
          dataQueryId: seedQueryId
        };
      }
    };
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== columnsSettings) {
    t2 = [columnsSettings];
    $[7] = columnsSettings;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[9] !== isResizing || $[10] !== props.columnStates || $[11] !== props.settings || $[12] !== queryId) {
    t3 = () => {
      if (!hasPresetBeenApplied.current) {
        return;
      }
      if (isResizing) {
        return;
      }
      if (isPresetCustom(activePresetRef.current)) {
        const savedPreset = activePresetRef.current as PresetDetail;
        const currentColumns = filterTechnicalArray(getPresetStates(props.columnStates));
        const isDirty = isCustomPresetDirty({
          columnsSettings: filterTechnicalArray(savedPreset.columnsSettings),
          gridSettings: savedPreset.gridSettings,
          queryId: savedPreset.dataQueryId
        }, {
          columnsSettings: currentColumns,
          gridSettings: props.settings,
          queryId: queryId ?? ""
        });
        storeRef.current.setIsPresetChanged(isDirty);
        return;
      }
      const searchParams = getSearchParamsByGridCode(gridCodeRef.current);
      const dataQueryId = searchParams?.queryId;
      if (!dataQueryId) {
        return;
      }
      const currentColumnSettings = filterTechnicalArray(getPresetStates(props.columnStates));
      const modifiedPreset = {
        columnsSettings: currentColumnSettings,
        gridSettings: props.settings,
        dataQueryId: lastVirtualPresetState.current?.dataQueryId ?? dataQueryId
      };
      if (isPresetColumnsSettingsInvalid(modifiedPreset)) {
        return;
      }
      const isPresetModified = isNilOrEmpty(lastVirtualPresetState.current) ? true : !isVirtualPresetEqual(lastVirtualPresetState.current, modifiedPreset);
      if (!isPresetModified) {
        return;
      }
      lastVirtualPresetState.current = modifiedPreset;
      httpRef.current.updateVirtualPreset(modifiedPreset)?.catch(_temp);
    };
    t4 = [props.columnStates, props.settings, isResizing, queryId];
    $[9] = isResizing;
    $[10] = props.columnStates;
    $[11] = props.settings;
    $[12] = queryId;
    $[13] = t3;
    $[14] = t4;
  } else {
    t3 = $[13];
    t4 = $[14];
  }
  useEffect(t3, t4);
};
function _temp() {}